import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSuccess('消息已发送，我们会尽快回复您！');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      setFormError('消息内容不能超过500个字符');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9sp4l94ne17ei0sdlg0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const code = (parsed as { code?: string })?.code;
      const meta = (parsed as { meta?: { message?: string; detail?: string } })?.meta;
      const serverMsg = meta?.message || meta?.detail || '';
      if (res.ok && code === 'OK') {
        setFormSuccess('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        setFormError(serverMsg || '发送失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于亚洲精品',
    description: '亚洲精品汇聚日韩、东南亚及华系顶尖品牌，为您精选最具品质与设计感的亚洲精品产品。',
    about: {
      '@type': 'Organization',
      name: '亚洲精品',
      foundingDate: '2018',
      description: '甄选日韩、东南亚及华系顶尖品牌，专注亚洲精品生活方式。',
      address: {
        '@type': 'PostalAddress',
        addressLocality: '上海',
        addressRegion: '上海',
        addressCountry: 'CN',
        postalCode: '200000',
        streetAddress: '静安区南京西路1266号恒隆广场办公楼2座15层',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-21-6888-8888',
        email: 'contact@yazhoujingpin.com',
        contactType: 'customer service',
        availableLanguage: ['Chinese', 'English', 'Japanese', 'Korean'],
      },
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      {/* Page Header */}
      <section className="relative py-12 md:py-16 bg-background-100 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20Asian%20brand%20office%20space%20with%20warm%20cream%20walls%2C%20wooden%20furniture%2C%20soft%20natural%20lighting%2C%20premium%20atmosphere%2C%20abstract%20background&width=1800&height=400&seq=about-header&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">关于我们</h1>
          <p className="mt-2 text-sm text-foreground-500">了解亚洲精品的故事与使命</p>
        </div>
      </section>

      {/* Brand Story */}
      <section id="story" className="py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full whitespace-nowrap">品牌故事</span>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-900">让世界看见亚洲的精致之美</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20modern%20Asian%20workspace%20with%20curated%20products%20displayed%20on%20wooden%20shelves%2C%20warm%20cream%20walls%2C%20soft%20natural%20lighting%2C%20premium%20boutique%20atmosphere%2C%20editorial%20interior%20photography&width=600&height=450&seq=about-story-img&orientation=landscape"
                alt="亚洲精品品牌故事"
                className="w-full rounded-lg object-cover"
              />
            </div>
            <div className="space-y-4">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                亚洲精品成立于2018年，源于创始团队对亚洲美学的深深热爱。我们走遍日本京都的匠人工坊、韩国首尔的设计师工作室、曼谷的手工艺市集、台北的文创园区——只为寻找那些真正代表亚洲设计与品质的精品好物。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                我们相信，亚洲不仅有悠久的工艺传统，更有面向未来的创新力量。从传统手工艺的现代演绎到尖端科技的美妆研发，亚洲品牌正在全球舞台上展现前所未有的魅力。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                <strong>亚洲精品</strong>的使命是成为连接亚洲顶尖品牌与全球消费者的桥梁，让每一件精选的亚洲精品都能找到懂得欣赏它的人。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section id="reputation" className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-medium text-accent-600 bg-accent-50 px-3 py-1.5 rounded-full whitespace-nowrap">品牌信誉</span>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-900">品质承诺与行业认可</h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-2xl mx-auto">我们用实际行动赢得每一位顾客的信任</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-medal-line',
                title: '严苛选品体系',
                desc: '每一件产品上架前都需通过7道选品流程。从品牌背景调查、产品品质测试、设计价值评估到用户反馈验证，我们的选品通过率不到15%。',
              },
              {
                icon: 'ri-git-repository-private-line',
                title: '正品保障',
                desc: '与品牌方直接合作，确保100%正品。每件产品都可追溯至品牌源头，支持品牌官方验证。假一赔十，让您购物无忧。',
              },
              {
                icon: 'ri-award-line',
                title: '行业荣誉',
                desc: '2024-2026年连续三年获评"亚洲跨境电商最佳精品平台"，被《时尚芭莎》《Elle》等国际媒体专题报道。累计服务超过50万高端会员。',
              },
            ].map((item) => (
              <div key={item.title} className="bg-background-50 rounded-xl p-6 md:p-8 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-accent-50 text-accent-600">
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">{item.title}</h3>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { media: '时尚芭莎', quote: '"亚洲精品是发现东方美学的窗口"' },
              { media: 'Elle', quote: '"重新定义了跨境电商的品质标准"' },
              { media: 'Vogue', quote: '"将亚洲设计带向世界的先行者"' },
              { media: 'Wallpaper*', quote: '"对细节的执着令人印象深刻"' },
            ].map((item) => (
              <div key={item.media} className="bg-background-50 rounded-lg p-5 text-center">
                <p className="text-sm text-foreground-600 italic leading-relaxed">&ldquo;{item.quote}&rdquo;</p>
                <p className="mt-2 text-xs font-medium text-primary-600">— {item.media}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-medium text-secondary-600 bg-secondary-100 px-3 py-1.5 rounded-full whitespace-nowrap">联系我们</span>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-900">与我们取得联系</h2>
            <p className="mt-3 text-sm text-foreground-500">我们期待听到您的声音</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">公司地址</h3>
                  <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
                    上海市静安区南京西路1266号<br />恒隆广场办公楼2座15层
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">客服热线</h3>
                  <p className="mt-1 text-sm text-foreground-600">+86 21-6888-8888</p>
                  <p className="text-xs text-foreground-400 mt-0.5">周一至周日 9:00 - 21:00</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">电子邮箱</h3>
                  <p className="mt-1 text-sm text-foreground-600">contact@yazhoujingpin.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">工作时间</h3>
                  <p className="mt-1 text-sm text-foreground-600">周一至周五：9:00 - 18:00</p>
                  <p className="text-sm text-foreground-600">周六：10:00 - 17:00</p>
                </div>
              </div>

              {/* OpeningHours Schema */}
              <script type="application/ld+json">
                {JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'Organization',
                  name: '亚洲精品',
                  address: {
                    '@type': 'PostalAddress',
                    streetAddress: '南京西路1266号恒隆广场办公楼2座15层',
                    addressLocality: '上海',
                    addressRegion: '上海',
                    postalCode: '200000',
                    addressCountry: 'CN',
                  },
                  telephone: '+86-21-6888-8888',
                  openingHoursSpecification: [
                    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '18:00' },
                    { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '10:00', closes: '17:00' },
                  ],
                })}
              </script>
            </div>

            {/* Contact Form */}
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>
              <form
                className="space-y-4"
                data-readdy-form
                onSubmit={handleContactSubmit}
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容（限500字符）</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="请输入您想说的话..."
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                {formError && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
                {formSuccess && (
                  <p className="text-sm text-green-600">{formSuccess}</p>
                )}
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
                >
                  发送消息
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}